import { createPortal } from 'react-dom';
import { useState, useEffect } from 'react';
import { X, Loader2, ImageIcon, Film, Music, Check } from 'lucide-react';
import api, { getImageUrl } from '@/lib/api';

/**
 * Universal "Use Existing Media" picker. Browse every media asset already created
 * anywhere in the project (frames, alternatives, videos, location refs, character
 * images) and pick one — no duplicate files are created, we reference the same path.
 */
export function MediaPicker({ open, onClose, projectId, mediaType = 'image', title = 'Use Existing Media', onSelect, frameTabs = false }) {
  const [loading, setLoading] = useState(false);
  const [media, setMedia] = useState([]);
  const [selecting, setSelecting] = useState(null);
  const [tab, setTab] = useState('all');

  useEffect(() => {
    if (!open || !projectId) return;
    setTab('all');
    setLoading(true);
    api.get(`/projects/${projectId}/media`, { params: { media_type: mediaType, _t: Date.now() } })
      .then(r => setMedia(r.data.media || []))
      .catch(() => setMedia([]))
      .finally(() => setLoading(false));
  }, [open, projectId, mediaType]);

  if (!open) return null;

  const pick = async (item) => {
    setSelecting(item.path);
    try { await onSelect(item); onClose(); }
    finally { setSelecting(null); }
  };

  const counts = {
    all: media.length,
    selected: media.filter(m => m.status === 'selected').length,
    rejected: media.filter(m => m.status === 'rejected').length,
  };
  const visible = frameTabs && tab !== 'all' ? media.filter(m => m.status === tab) : media;

  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-[2px]" onClick={onClose}>
      <div data-testid="media-picker" onClick={e => e.stopPropagation()}
        className="absolute right-0 top-0 h-full w-full max-w-[600px] bg-[#0E0E12] border-l border-white/10 shadow-2xl flex flex-col animate-slide-in-right overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <h3 className="text-white font-bold text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
          <button data-testid="media-picker-close" onClick={onClose} className="text-[#8A8A93] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {frameTabs && (
          <div className="flex gap-1 px-4 pt-3" data-testid="media-picker-tabs">
            {[{ k: 'all', l: 'All' }, { k: 'selected', l: 'Selected' }, { k: 'rejected', l: 'Rejected' }].map(t => (
              <button key={t.k} data-testid={`media-tab-${t.k}`} onClick={() => setTab(t.k)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${tab === t.k
                  ? (t.k === 'rejected' ? 'bg-red-500/15 text-red-300 border-red-500/30' : t.k === 'selected' ? 'bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30' : 'bg-[#FF4500]/15 text-[#FF4500] border-[#FF4500]/30')
                  : 'bg-white/[0.03] text-[#8A8A93] border-white/[0.06] hover:text-white'}`}>
                {t.l} <span className="opacity-60">({counts[t.k]})</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-[#8A8A93]"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : visible.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-[#55555C] gap-2">
              <ImageIcon className="w-8 h-8" />
              <p className="text-sm">{frameTabs && tab === 'rejected' ? 'No rejected frames yet — replaced or unused generations show up here.' : frameTabs && tab === 'selected' ? 'No frames selected yet.' : 'No media created in this project yet.'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {visible.map(item => (
                <button key={item.path} data-testid={`media-item-${item.path.split('/').pop()}`} onClick={() => pick(item)} disabled={selecting}
                  className="group relative rounded-xl overflow-hidden border border-white/[0.06] bg-[#141418] hover:border-[#FF4500]/50 transition-all text-left">
                  <div className="aspect-video bg-[#0a0a0e] flex items-center justify-center overflow-hidden">
                    {item.media_type === 'image' ? (
                      <img src={getImageUrl(item.path)} alt={item.label} className="w-full h-full object-cover" loading="lazy" />
                    ) : item.media_type === 'video' ? (
                      <video src={getImageUrl(item.path)} className="w-full h-full object-cover" muted preload="metadata" />
                    ) : (
                      <Music className="w-8 h-8 text-[#A78BFA]" />
                    )}
                    {selecting === item.path && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#FF4500]" /></div>
                    )}
                    <div className="absolute inset-0 bg-[#FF4500]/0 group-hover:bg-[#FF4500]/10 flex items-center justify-center transition-colors">
                      <span className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-[11px] font-medium text-white bg-[#FF4500] px-2 py-1 rounded-full transition-opacity"><Check className="w-3 h-3" /> Use</span>
                    </div>
                  </div>
                  <div className="p-2">
                    <p className="text-[10px] text-[#C9C9D0] truncate flex items-center gap-1">
                      {item.media_type === 'video' ? <Film className="w-3 h-3 shrink-0" /> : item.media_type === 'audio' ? <Music className="w-3 h-3 shrink-0" /> : <ImageIcon className="w-3 h-3 shrink-0" />}
                      {item.label}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
